import { useCallback, useEffect, useRef, useState } from 'react';
import {
  CallMedia,
  CallStatus,
  ErrorCode,
  ROUTES,
  SOCKET_EVENTS,
  errorMessage,
  type Call,
  type IceServers,
} from '@forever/shared';
import { api } from '@/lib/api';
import { connectSocket } from '@/lib/socket';

/**
 * Panelde sesli ve görüntülü görüşme.
 *
 * Bu dosya ürünün eksik yarısıydı. Mobil taraf baştan beri arayabiliyordu ama
 * panelde hiç WebRTC yoktu: danışan zili çalıyor, diyetisyenin cevaplayacak
 * bir şeyi olmuyordu. "Sesli arama çalışmıyor" geri bildiriminin sebebi bu.
 *
 * Sunucu medya taşımıyor; yalnızca SDP ve ICE paketlerini aktarıyor
 * (`apps/api/src/realtime/calls.ts`). Protokol mobildekiyle birebir aynı —
 * `apps/mobile/src/features/calls/useWebRTCCall.ts` ile yan yana okunmalı;
 * biri değişirse diğeri de değişmeli.
 *
 * Akış — arayan taraf:
 *   izin → CALL_INVITE → CALL_STATE(RINGING) → CALL_ACCEPTED → CALL_OFFER
 *   gönder → CALL_ANSWER al → bağlanınca 'active'
 *
 * Akış — aranan taraf:
 *   CALL_INCOMING → izin → CALL_ACCEPT → CALL_OFFER gelir → CALL_ANSWER
 *   gönder → bağlanınca 'active'
 */

/* ---------------------------------- tipler --------------------------------- */

export type CallPhase =
  | 'idle'
  | 'requesting-permission'
  | 'calling'
  | 'ringing'
  | 'connecting'
  | 'active'
  | 'ended'
  | 'failed';

export type CallEndReason =
  | 'completed'
  | 'declined'
  | 'missed'
  | 'peer-offline'
  | 'permission-denied'
  | 'no-device'
  | 'failed'
  | 'cancelled';

export interface CallState {
  phase: CallPhase;
  media: CallMedia;
  callId: string | null;
  peerName: string | null;
  durationSeconds: number;
  endReason: CallEndReason | null;
  errorText: string | null;
  /** Karşı tarafın kamerası açık mı — uzak görüntü akışı var mı. */
  remoteVideoOn: boolean;
  micOn: boolean;
  cameraOn: boolean;
}

interface CallSignalPayload {
  callId: string;
  sdp?: string;
  candidate?: RTCIceCandidateInit;
}

interface CallStatePayload {
  callId: string;
  status: CallStatus;
}

interface CallClosedPayload {
  callId: string;
  reason?: string;
}

interface SocketErrorPayload {
  code: ErrorCode;
  message?: string;
  callId?: string;
}

/** TURN alınamazsa en azından aynı ağdaki iki uç buluşabilsin. */
const FALLBACK_ICE: RTCIceServer[] = [{ urls: 'stun:stun.l.google.com:19302' }];

const BASE_STATE: CallState = {
  phase: 'idle',
  media: CallMedia.AUDIO,
  callId: null,
  peerName: null,
  durationSeconds: 0,
  endReason: null,
  errorText: null,
  remoteVideoOn: false,
  micOn: true,
  cameraOn: true,
};

/* --------------------------------- yardımcı -------------------------------- */

/**
 * Tarayıcı izin hatasını sebebe çevirir.
 *
 * `NotAllowedError` kullanıcının reddetmesi, `NotFoundError` cihazın hiç
 * olmaması. İkisi farklı şeyler: birinde "izin verin" demek doğru, diğerinde
 * kullanıcının yapabileceği bir şey yok.
 */
function mediaErrorReason(error: unknown): CallEndReason {
  const name = (error as { name?: string } | null)?.name;
  if (name === 'NotAllowedError' || name === 'SecurityError') return 'permission-denied';
  if (name === 'NotFoundError' || name === 'OverconstrainedError') return 'no-device';
  return 'failed';
}

function reasonText(reason: CallEndReason, media: CallMedia): string | null {
  switch (reason) {
    case 'permission-denied':
      return media === CallMedia.VIDEO
        ? 'Kamera ve mikrofon izni verilmedi. Tarayıcının adres çubuğundaki izin simgesinden açabilirsiniz.'
        : 'Mikrofon izni verilmedi. Tarayıcının adres çubuğundaki izin simgesinden açabilirsiniz.';
    case 'no-device':
      return media === CallMedia.VIDEO
        ? 'Kamera veya mikrofon bulunamadı.'
        : 'Mikrofon bulunamadı.';
    case 'peer-offline':
      return 'Karşı taraf şu anda çevrimiçi değil.';
    case 'declined':
      return 'Arama reddedildi.';
    case 'missed':
      return 'Karşı taraf cevap vermedi.';
    case 'failed':
      return 'Görüşme kurulamadı.';
    default:
      return null;
  }
}

/* ---------------------------------- kanca ---------------------------------- */

export function useBrowserCall() {
  const [state, setState] = useState<CallState>(BASE_STATE);

  /* Render'ı tetiklemeden okunması gereken değerler ref'te; olay
     işleyicileri kapanış (closure) içinde eski state'i görmesin diye. */
  const pcRef = useRef<RTCPeerConnection | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const remoteStreamRef = useRef<MediaStream | null>(null);
  const callIdRef = useRef<string | null>(null);
  const phaseRef = useRef<CallPhase>('idle');
  const mediaRef = useRef<CallMedia>(CallMedia.AUDIO);
  /**
   * Bu görüşmede arayan mıyız? Sunucu CALL_ACCEPTED'ı iki tarafa da
   * gönderiyor; teklifi yalnızca arayan üretmeli. Aranan taraf da teklif
   * üretirse iki teklif çakışır ve bağlantı kurulmaz (telefondan gelen
   * aramayı panelde cevaplamak bu yüzden başarısız oluyordu).
   */
  const callerRef = useRef(false);
  const activeSinceRef = useRef<number | null>(null);
  /** Uzak açıklama gelmeden önce düşen ICE adayları burada bekler. */
  const pendingIceRef = useRef<RTCIceCandidateInit[]>([]);
  const remoteDescSetRef = useRef(false);

  /*
   * Video/ses elemanları arama penceresiyle birlikte (ve uzak görüntü ancak
   * geldikten sonra) ekrana basılıyor; akış elemandan önce hazır olabilir.
   * Geri çağırmalı ref'ler eleman takıldığı anda güncel akışı bağlar —
   * düz ref'le akış boşa atanıp görüntü siyah kalıyordu.
   */
  const localVideoEl = useRef<HTMLVideoElement | null>(null);
  const remoteVideoEl = useRef<HTMLVideoElement | null>(null);
  const remoteAudioEl = useRef<HTMLAudioElement | null>(null);
  const localVideoRef = useCallback((el: HTMLVideoElement | null) => {
    localVideoEl.current = el;
    if (el && el.srcObject !== localStreamRef.current) el.srcObject = localStreamRef.current;
  }, []);
  const remoteVideoRef = useCallback((el: HTMLVideoElement | null) => {
    remoteVideoEl.current = el;
    if (el && el.srcObject !== remoteStreamRef.current) el.srcObject = remoteStreamRef.current;
  }, []);
  const remoteAudioRef = useCallback((el: HTMLAudioElement | null) => {
    remoteAudioEl.current = el;
    if (el && el.srcObject !== remoteStreamRef.current) el.srcObject = remoteStreamRef.current;
  }, []);

  const setPhase = useCallback((phase: CallPhase) => {
    phaseRef.current = phase;
    setState((s) => ({ ...s, phase }));
  }, []);

  /* ------------------------------- temizlik -------------------------------- */

  const teardown = useCallback(() => {
    pcRef.current?.getSenders().forEach((sender) => sender.track?.stop());
    pcRef.current?.close();
    pcRef.current = null;

    localStreamRef.current?.getTracks().forEach((t) => t.stop());
    localStreamRef.current = null;
    remoteStreamRef.current = null;

    if (localVideoEl.current) localVideoEl.current.srcObject = null;
    if (remoteVideoEl.current) remoteVideoEl.current.srcObject = null;
    if (remoteAudioEl.current) remoteAudioEl.current.srcObject = null;

    pendingIceRef.current = [];
    remoteDescSetRef.current = false;
    activeSinceRef.current = null;
  }, []);

  const finish = useCallback(
    (reason: CallEndReason, errorText?: string) => {
      teardown();
      const media = mediaRef.current;
      phaseRef.current = reason === 'completed' ? 'ended' : 'failed';
      setState((s) => ({
        ...s,
        phase: reason === 'completed' ? 'ended' : 'failed',
        endReason: reason,
        errorText: errorText ?? reasonText(reason, media),
      }));
      callIdRef.current = null;
    },
    [teardown],
  );

  /* ------------------------------ medya erişimi ----------------------------- */

  const acquireStream = useCallback(async (media: CallMedia): Promise<MediaStream> => {
    const constraints: MediaStreamConstraints = {
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      video:
        media === CallMedia.VIDEO
          ? { width: { ideal: 1280 }, height: { ideal: 720 }, facingMode: 'user' }
          : false,
    };
    const stream = await navigator.mediaDevices.getUserMedia(constraints);
    localStreamRef.current = stream;
    if (localVideoEl.current) localVideoEl.current.srcObject = stream;
    return stream;
  }, []);

  /* --------------------------- bağlantı kurulumu ---------------------------- */

  const emitSignal = useCallback((event: string, payload: Record<string, unknown>) => {
    connectSocket().emit(event, payload);
  }, []);

  const buildPeerConnection = useCallback(
    async (callId: string, stream: MediaStream): Promise<RTCPeerConnection> => {
      let iceServers = FALLBACK_ICE;
      try {
        const res = await api.get<IceServers>(ROUTES.calls.iceServers);
        if (res.iceServers.length > 0) iceServers = res.iceServers as RTCIceServer[];
      } catch {
        // TURN alınamadı; STUN ile devam. Aynı ağdaki uçlar yine buluşur.
      }

      const pc = new RTCPeerConnection({ iceServers, iceCandidatePoolSize: 2 });
      stream.getTracks().forEach((track) => pc.addTrack(track, stream));

      pc.onicecandidate = (event) => {
        if (!event.candidate) return;
        emitSignal(SOCKET_EVENTS.CALL_ICE, {
          callId,
          candidate: event.candidate.toJSON(),
        });
      };

      pc.ontrack = (event) => {
        const [remote] = event.streams;
        if (!remote) return;
        remoteStreamRef.current = remote;

        /* Ses ve görüntü ayrı elemanlara bağlanıyor: sesli görüşmede
           video elemanı hiç basılmıyor, ses yine de çalmalı. */
        if (remoteAudioEl.current) remoteAudioEl.current.srcObject = remote;
        if (remoteVideoEl.current) remoteVideoEl.current.srcObject = remote;

        setState((s) => ({ ...s, remoteVideoOn: remote.getVideoTracks().length > 0 }));
      };

      pc.onconnectionstatechange = () => {
        if (pc.connectionState === 'connected') {
          activeSinceRef.current = Date.now();
          setPhase('active');
        }
        if (pc.connectionState === 'failed') finish('failed');
      };

      pcRef.current = pc;
      return pc;
    },
    [emitSignal, finish, setPhase],
  );

  const flushPendingIce = useCallback(async () => {
    const pc = pcRef.current;
    if (!pc || !remoteDescSetRef.current) return;
    const queued = pendingIceRef.current;
    pendingIceRef.current = [];
    for (const candidate of queued) {
      try {
        await pc.addIceCandidate(new RTCIceCandidate(candidate));
      } catch {
        // Geçersiz aday bağlantıyı düşürmez; diğer adaylarla devam.
      }
    }
  }, []);

  /* --------------------------------- başlat --------------------------------- */

  const start = useCallback(
    async (conversationId: string, media: CallMedia, peerName: string) => {
      mediaRef.current = media;
      callerRef.current = true;
      setState({ ...BASE_STATE, phase: 'requesting-permission', media, peerName });
      phaseRef.current = 'requesting-permission';

      let stream: MediaStream;
      try {
        stream = await acquireStream(media);
      } catch (error) {
        finish(mediaErrorReason(error));
        return;
      }

      setPhase('calling');
      emitSignal(SOCKET_EVENTS.CALL_INVITE, { conversationId, media });

      /* Peer connection CALL_ACCEPTED gelince kuruluyor: karşı taraf
         açmadan kamera/mikrofon akışını taşımaya başlamanın anlamı yok. */
      void stream;
    },
    [acquireStream, emitSignal, finish, setPhase],
  );

  /* --------------------------------- cevapla -------------------------------- */

  const accept = useCallback(
    async (call: Call) => {
      const media = call.media;
      mediaRef.current = media;
      callerRef.current = false;
      callIdRef.current = call.id;
      setState({
        ...BASE_STATE,
        phase: 'requesting-permission',
        media,
        callId: call.id,
        peerName: call.initiatorName,
      });
      phaseRef.current = 'requesting-permission';

      try {
        await acquireStream(media);
      } catch (error) {
        emitSignal(SOCKET_EVENTS.CALL_DECLINE, { callId: call.id });
        finish(mediaErrorReason(error));
        return;
      }

      setPhase('connecting');
      emitSignal(SOCKET_EVENTS.CALL_ACCEPT, { callId: call.id });
    },
    [acquireStream, emitSignal, finish, setPhase],
  );

  const decline = useCallback(
    (callId: string) => {
      emitSignal(SOCKET_EVENTS.CALL_DECLINE, { callId });
      finish('declined');
    },
    [emitSignal, finish],
  );

  const hangUp = useCallback(
    (reason: string = 'user-hangup') => {
      const id = callIdRef.current;
      if (id) emitSignal(SOCKET_EVENTS.CALL_END, { callId: id, reason });
      finish('completed');
    },
    [emitSignal, finish],
  );

  /* ------------------------------ mikrofon/kamera --------------------------- */

  const toggleMic = useCallback(() => {
    const track = localStreamRef.current?.getAudioTracks()[0];
    if (!track) return;
    track.enabled = !track.enabled;
    setState((s) => ({ ...s, micOn: track.enabled }));
  }, []);

  const toggleCamera = useCallback(() => {
    const track = localStreamRef.current?.getVideoTracks()[0];
    if (!track) return;
    track.enabled = !track.enabled;
    setState((s) => ({ ...s, cameraOn: track.enabled }));
  }, []);

  /* ------------------------------ sinyalleşme ------------------------------- */

  useEffect(() => {
    const socket = connectSocket();

    const isOurs = (id: string) => callIdRef.current === id;

    const onCallState = (p: CallStatePayload) => {
      if (!callIdRef.current) callIdRef.current = p.callId;
      if (!isOurs(p.callId)) return;
      setState((s) => ({ ...s, callId: p.callId }));
      if (p.status === CallStatus.RINGING) setPhase('ringing');
    };

    /* Karşı taraf açtı: teklifi biz üretiyoruz (arayan taraf). */
    const onAccepted = async (p: CallStatePayload) => {
      if (!isOurs(p.callId) || !callerRef.current || pcRef.current) return;
      const stream = localStreamRef.current;
      if (!stream) return finish('failed');

      setPhase('connecting');
      const pc = await buildPeerConnection(p.callId, stream);
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      emitSignal(SOCKET_EVENTS.CALL_OFFER, { callId: p.callId, sdp: offer.sdp });
    };

    /* Teklif geldi: aranan taraftayız, yanıtı üretiyoruz. */
    const onOffer = async (p: CallSignalPayload) => {
      if (!isOurs(p.callId) || !p.sdp) return;
      const stream = localStreamRef.current;
      if (!stream) return finish('failed');

      const pc = pcRef.current ?? (await buildPeerConnection(p.callId, stream));
      await pc.setRemoteDescription(new RTCSessionDescription({ type: 'offer', sdp: p.sdp }));
      remoteDescSetRef.current = true;
      await flushPendingIce();

      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      emitSignal(SOCKET_EVENTS.CALL_ANSWER, { callId: p.callId, sdp: answer.sdp });
    };

    const onAnswer = async (p: CallSignalPayload) => {
      if (!isOurs(p.callId) || !p.sdp || !pcRef.current) return;
      await pcRef.current.setRemoteDescription(
        new RTCSessionDescription({ type: 'answer', sdp: p.sdp }),
      );
      remoteDescSetRef.current = true;
      await flushPendingIce();
    };

    const onIce = async (p: CallSignalPayload) => {
      if (!isOurs(p.callId) || !p.candidate) return;
      /* Uzak açıklama henüz yoksa aday sıraya giriyor: erken gelen adayı
         eklemek istisna fırlatır ve bağlantı kurulmaz. */
      if (!pcRef.current || !remoteDescSetRef.current) {
        pendingIceRef.current.push(p.candidate);
        return;
      }
      try {
        await pcRef.current.addIceCandidate(new RTCIceCandidate(p.candidate));
      } catch {
        // yoksayılır
      }
    };

    const onDeclined = (p: CallClosedPayload) => {
      if (!isOurs(p.callId)) return;
      finish(p.reason === 'timeout' ? 'missed' : 'declined');
    };

    const onEnded = (p: CallClosedPayload) => {
      if (!isOurs(p.callId)) return;
      finish('completed');
    };

    const onError = (p: SocketErrorPayload) => {
      const live =
        phaseRef.current === 'calling' ||
        phaseRef.current === 'ringing' ||
        phaseRef.current === 'connecting';
      if (!live && !(p.callId && isOurs(p.callId))) return;
      if (p.code === ErrorCode.PEER_OFFLINE) return finish('peer-offline');
      finish('failed', p.message ?? errorMessage(p.code));
    };

    const bindings: Array<[string, (payload: unknown) => void]> = [
      [SOCKET_EVENTS.CALL_STATE, (p) => onCallState(p as CallStatePayload)],
      [SOCKET_EVENTS.CALL_ACCEPTED, (p) => void onAccepted(p as CallStatePayload)],
      [SOCKET_EVENTS.CALL_OFFER, (p) => void onOffer(p as CallSignalPayload)],
      [SOCKET_EVENTS.CALL_ANSWER, (p) => void onAnswer(p as CallSignalPayload)],
      [SOCKET_EVENTS.CALL_ICE, (p) => void onIce(p as CallSignalPayload)],
      [SOCKET_EVENTS.CALL_DECLINED, (p) => onDeclined(p as CallClosedPayload)],
      [SOCKET_EVENTS.CALL_ENDED, (p) => onEnded(p as CallClosedPayload)],
      [SOCKET_EVENTS.ERROR, (p) => onError(p as SocketErrorPayload)],
    ];

    for (const [event, handler] of bindings) socket.on(event, handler);
    return () => {
      for (const [event, handler] of bindings) socket.off(event, handler);
    };
  }, [buildPeerConnection, emitSignal, finish, flushPendingIce, setPhase]);

  /* -------------------------------- süre sayacı ----------------------------- */

  useEffect(() => {
    if (state.phase !== 'active') return undefined;
    const tick = () =>
      setState((s) => ({
        ...s,
        durationSeconds: activeSinceRef.current
          ? Math.max(0, Math.round((Date.now() - activeSinceRef.current) / 1000))
          : 0,
      }));
    tick();
    const timer = window.setInterval(tick, 1000);
    return () => window.clearInterval(timer);
  }, [state.phase]);

  /* Sekme kapanırsa karşı taraf sonsuza kadar çalmasın. */
  useEffect(() => {
    const onUnload = () => {
      const id = callIdRef.current;
      if (id) connectSocket().emit(SOCKET_EVENTS.CALL_END, { callId: id, reason: 'tab-closed' });
    };
    window.addEventListener('beforeunload', onUnload);
    return () => {
      window.removeEventListener('beforeunload', onUnload);
      teardown();
    };
  }, [teardown]);

  const reset = useCallback(() => {
    teardown();
    callIdRef.current = null;
    phaseRef.current = 'idle';
    setState(BASE_STATE);
  }, [teardown]);

  return {
    state,
    start,
    accept,
    decline,
    hangUp,
    toggleMic,
    toggleCamera,
    reset,
    localVideoRef,
    remoteVideoRef,
    remoteAudioRef,
  };
}
